// Status banner for regional brand enrichment. Shows when the
// edge function is mid-pass for the user's primary shopping city.
// Lives in Settings (under "WHERE YOU SHOP") + BrandStep (compact).
// Renders nothing when there's no city set or the row is in a
// non-active state.
//
// Animated hourglass + prominent progress bar + N/M readout so the
// user can see at a glance whether the background work is actually
// progressing or has stalled. Replaces the prior generic spinner +
// thin progress bar.
//
// Self-contained: reads useAuth + useProfile internally so it can
// drop in anywhere without prop drilling. Subscribes to the row via
// useRegionalEnrichmentStatus (postgres_changes realtime) — the
// edge function upserts at every chunk boundary so the bar ticks
// live as canonicals complete.

import { useAuth } from "../lib/useAuth";
import { useProfile } from "../lib/useProfile";
import { useRegionalEnrichmentStatus, progressFromRow } from "../lib/useRegionalEnrichmentStatus";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";

export default function RegionalEnrichmentBanner({ regionKey: explicitKey, compact = false }) {
  const { userId } = useAuth();
  const { profile } = useProfile(userId);

  // Primary city: caller can pass an explicit regionKey, otherwise
  // we fall back to the first city the user picked.
  const cities = Array.isArray(profile?.shopping_cities) ? profile.shopping_cities : [];
  const regionKey = explicitKey || cities[0] || null;

  const row = useRegionalEnrichmentStatus(regionKey);
  const progress = progressFromRow(row);

  // Show in_progress AND queued so the user gets feedback the
  // moment they tap Re-fire (status flips queued → in_progress
  // after the first edge-fn invocation hits the lock acquire).
  // queued = "we know about it, hasn't kicked off yet".
  if (!progress) return null;
  if (progress.status !== "in_progress" && progress.status !== "queued") return null;

  const accent = CHIP_TONES.location.fg;
  const cityShortName = (progress.displayName || "").split(",")[0] || "your area";
  const isQueued = progress.status === "queued";

  return (
    <>
      <style>{`
        @keyframes mcm-hourglass-flip {
          0%, 45%   { transform: rotate(0deg); }
          50%, 95%  { transform: rotate(180deg); }
          100%      { transform: rotate(360deg); }
        }
        @keyframes mcm-hourglass-grain {
          0%   { transform: translateY(-3px); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(8px);  opacity: 0; }
        }
        @keyframes mcm-region-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1.0; }
        }
        @keyframes mcm-region-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .mcm-hourglass-rot {
          animation: mcm-hourglass-flip 6s cubic-bezier(0.65, 0, 0.35, 1) infinite;
          transform-origin: 12px 12px;
        }
        .mcm-hourglass-grain {
          animation: mcm-hourglass-grain 0.9s linear infinite;
        }
        .mcm-region-pulse {
          animation: mcm-region-pulse 1.6s ease-in-out infinite;
        }
        .mcm-region-shimmer {
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
          animation: mcm-region-shimmer 1.6s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .mcm-hourglass-rot,
          .mcm-hourglass-grain,
          .mcm-region-pulse,
          .mcm-region-shimmer { animation: none; }
        }
      `}</style>

      <div
        role="status"
        aria-live="polite"
        style={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 10 : 12,
          padding: compact ? "10px 12px" : "12px 14px",
          background: `${accent}10`,
          border: `1px solid ${accent}55`,
          borderRadius: 12,
        }}
      >
        {/* Hourglass — sand draining. Visual signal that something's
            running. The pct prop is now always 50 so the sand sits
            half-and-half — we no longer poll for live progress (the
            polling burned ~21K queries/day on the Nano-tier disk
            budget for a "ticks every 30s" UX cue). The user gets a
            toast when status flips done; here, they just need to
            know "in progress, give it time." */}
        <Hourglass tint={accent} pct={50} large={!compact} />

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{
            fontFamily: FONT.forma,
            fontSize: compact ? 9 : 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: accent,
            fontWeight: 700,
          }}>
            {isQueued ? "Queued" : "Finding local brands"}
          </span>

          <div style={{
            fontFamily: FONT.forma, fontStyle: "italic", fontWeight: 500,
            fontSize: compact ? 12 : 13, color: COLOR.ink, lineHeight: 1.35,
          }}>
            {isQueued
              ? `Queued for ${cityShortName}. Picking up shortly.`
              : `Reading every canonical for ${cityShortName} brands. We'll let you know when it's done — keep using the app.`}
          </div>
        </div>
      </div>
    </>
  );
}

// Animated hourglass — actual SVG with sand falling. Top half
// drains as percentage rises, bottom fills. The whole svg flips
// every 6 seconds so the rotation reads as "still running" even
// when the percentage is moving slowly.
function Hourglass({ tint, pct, large = false }) {
  const size = large ? 28 : 22;
  const sandFill = Math.max(0, Math.min(100, Number(pct) || 0));
  const topSandH    = (1 - sandFill / 100) * 6;
  const bottomSandH = (sandFill / 100) * 6;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      style={{ flexShrink: 0, color: tint }}
      aria-hidden
    >
      <g className="mcm-hourglass-rot">
        <path d="M5 3 L19 3 L13 11 L11 11 Z"  fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M5 21 L19 21 L13 13 L11 13 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <line x1="4" y1="3"  x2="20" y2="3"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="4" y1="21" x2="20" y2="21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path
          d={`M${5 + (6 - topSandH) * 0.8} ${4 + (6 - topSandH)} L${19 - (6 - topSandH) * 0.8} ${4 + (6 - topSandH)} L13 11 L11 11 Z`}
          fill={tint}
          opacity="0.85"
        />
        <path
          d={`M${5 + (6 - bottomSandH) * 0.8} ${20 - (6 - bottomSandH)} L${19 - (6 - bottomSandH) * 0.8} ${20 - (6 - bottomSandH)} L13 13 L11 13 Z`}
          fill={tint}
          opacity="0.85"
        />
        <line
          className="mcm-hourglass-grain"
          x1="12" y1="11" x2="12" y2="13"
          stroke={tint} strokeWidth="1.4" strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
